import React, { useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { useForm } from 'informed'
import isEqual from 'lodash/isEqual'

import useModVerRemount from './useModVerRemount'
import { SmartSubmitContext, useSmartSubmit } from './useSmartSubmit'


const Form = props => {
	const { children, render, smartSubmit, reinitByModVer, onSubmit, initialValues, updateByInitial, onServerError, ...formProps } = props

	const mountInitial = useRef()
	useEffect(() => {
		mountInitial.current = initialValues
	}, [])

	const [loaded, setLoaded] = useState(false)
	useEffect(() => {
		if(updateByInitial && !isEqual(mountInitial.current, initialValues)) {
			const { values, submits } = formState
			const needUpdateVals = !submits && !isEqual(initialValues, values)
			if(!loaded) {
				setLoaded(true)
				if(needUpdateVals) {
					formApi.setValues(initialValues)
				}
			}
		}
	}, [initialValues, updateByInitial]) // eslint-disable-line react-hooks/exhaustive-deps

	const handleSubmit = values => {
		if(onSubmit) {
			/*
				on unmount informed clears all field values from original values object
				so we copy values object sent to onSubmit
			*/
			const result = onSubmit({ ...values })
			// Set error messages to fields returned in 'invalidArgs' of server errors response
			if(result && typeof result.catch === 'function') {
				result.catch(errors => {
					return errors?.graphQLErrors?.forEach(error => {
						const { extensions, message } = error
						if(extensions.code === 'BAD_USER_INPUT' && extensions.invalidArgs) {
							extensions.invalidArgs.forEach(field => {
								try {
									if(onServerError) {
										onServerError(formApi, field, error)
									} else {
										formApi.setError(field, message)
									}
								} catch(e) { /* Field isn't exists in Form */ }
							})
						}
					})
				})
			}
		}
	}

	const {
		formController,
		render: renderForm,
		userProps,
		formApi,
		formState
	} = useForm({
		onSubmit: handleSubmit,
		initialValues,
		allowEmptyStrings: true,
		...formProps
	})

	useModVerRemount({
		reinitByModVer,
		formApi,
		initialValues: formProps.initialValues
	})

	const smartSubmitCtxValue = useSmartSubmit({
		formController,
		formApi,
		formState,
		initialValues: formProps.initialValues,
		onSubmit: handleSubmit
	})

	const body = render || children

	return renderForm(
		<form
			noValidate
			{...userProps}
			onReset={formController.reset}
			onSubmit={formController.submitForm}
			onKeyDown={formController.keyDown}
		>
			<SmartSubmitContext.Provider value={smartSubmit && smartSubmitCtxValue}>
				{typeof body === 'function' ? body({ formApi, formState }) : body}
			</SmartSubmitContext.Provider>
		</form>
	)
}

Form.propTypes = {
	initialValues: PropTypes.object,
	onSubmit: PropTypes.func,
	smartSubmit: PropTypes.bool,
	reinitByModVer: PropTypes.bool,
	updateByInitial: PropTypes.bool
}

Form.defaultProps = {
	reinitByModVer: true,
	updateByInitial: true
}

export default Form
